import React from "react";
import type { SectionCfg, SectionOptions } from "./types";
import { Shell } from "./TemplateSections";
import type { ThemeTokens } from "./types";
import { E, EL, EditSessionUndoChip, inlineHistoryShortcut, openTemplateButtonHref, useEdit, useFieldTextUndo } from "./InlineEdit";
import { useTemplateSectionInstance } from "./templateData";
import { stripUnsafeHtml } from "./websiteEditorVibeGenerate";
import { extraSectionLabelForCatalog, isSchemaDemoText, isSchemaTabLabelPlaceholder, isSchemaTextBodyPlaceholder, parseDiscountExpiryDate, SCHEMA_DEMO, SCHEMA_DRAFT_HINT, schemaCtaHasLink, schemaDrawerText, schemaItemsArePristine, schemaPreviewText } from "./customWebsitePages";
import { SchemaFollowerPlatformIcon } from "./schemaFollowerPlatformIcon";
import {
  TemplateMarqueeTrack,
  shouldMarqueeTicker,
  type TemplateMarqueeTrackHandle,
} from "./TemplateMarqueeTrack";
import { btnClass } from "./sections/sectionUtils";
import { useTemplateAnalyticsOptional } from "./TemplateAnalyticsContext";
import { TEMPLATE_ANALYTICS_EVENTS } from "../../../Utils/SupabaseServices/templateAnalyticsEvents";
import {
  bannerImageFrameStyle,
  resolveBannerImageFrame,
} from "./templateBannerUtils";
import { SCHEMA_CTA_LABEL_DEFAULT } from "./websiteEditorCtaDefaults";
import {
  attachSrcDocEmbedHeight,
  buildEmbedSrcDoc,
  isSelfSizingEmbedHtml,
  listenForSbEmbedSize,
} from "../../../Utils/helpers/embedCodeHtml";
/**
 * Carousel arrows only when tiles actually extend past the visible strip.
 * Prefer last-cell geometry over scrollWidth — subpixel / scrollbar noise made
 * desktop show dead arrows when only 2 images fit (phone overflow still works).
 */
function galleryStripNeedsArrows(el: HTMLElement) {
  const cells = el.querySelectorAll(".mte-schema-gal-cell");
  if (cells.length < 2) return false;
  const last = cells[cells.length - 1] as HTMLElement;
  const overflow = last.offsetLeft + last.offsetWidth - el.clientWidth;
  return overflow > 8;
}

/** How far the strip can still move — used to disable end arrows. */
function galleryStripScrollEdges(el: HTMLElement) {
  const max = Math.max(0, el.scrollWidth - el.clientWidth);
  const left = el.scrollLeft;
  // Subpixel / rubber-band slack so arrows flip off before empty peek.
  const slack = 4;
  return {
    max,
    canPrev: left > slack,
    canNext: left < max - slack,
  };
}

/** Schema list rows: string fields plus an optional preview flag. */
type SchemaRow = {
  id?: string;
  _demo?: boolean;
  [key: string]: string | boolean | undefined;
};

type GalleryItem = {
  id?: string;
  photo?: string;
  caption?: string;
  imageZoom?: number | string;
  imageX?: number | string;
  imageY?: number | string;
};

function DiscountCopyButton({ code }: { code: string }) {
  const [copied, setCopied] = React.useState(false);
  const trackDiscount = usePublicSchemaClick("Discount");

  React.useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 1600);
    return () => window.clearTimeout(timer);
  }, [copied]);

  return (
    <button
      type="button"
      className="mte-schema-discount-copy"
      onClick={(event) => {
        event.stopPropagation();
        void trackDiscount(code || "Copy code", code);
        void navigator.clipboard?.writeText(code)?.then(() => setCopied(true));
      }}
    >
      {copied ? "Copied" : "Copy code"}
    </button>
  );
}

const TabsClickCtx = React.createContext<
  ((label: string, id: string) => void | Promise<unknown>) | null
>(null);

function usePublicSchemaClick(sectionType: string) {
  const { editable } = useEdit();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();
  const sectionId =
    String(sectionPath || "").split("/").pop() || sectionType;
  return React.useCallback(
    (label: string, id?: string) => {
      if (editable) return Promise.resolve();
      const name = String(label || "").trim();
      if (!name) return Promise.resolve();
      return (
        templateAnalytics?.trackSectionEvent(
          TEMPLATE_ANALYTICS_EVENTS.SECTION_CLICK,
          {
            sectionId,
            sectionType,
            entityType: "section",
            entityId: id || name,
            entityLabel: name,
          },
        ) ?? Promise.resolve()
      );
    },
    [editable, sectionId, sectionType, templateAnalytics],
  );
}

function schemaAnchorClick(
  event: React.MouseEvent,
  track: ((label: string, id: string) => void | Promise<unknown>) | null,
) {
  if (!track) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const anchor = target.closest("a");
  if (!anchor) return;
  const label =
    (anchor.textContent || "").replace(/\s+/g, " ").trim() ||
    anchor.getAttribute("href") ||
    "Link";
  const id = anchor.getAttribute("href") || label;
  const sameTab = !anchor.target || anchor.target === "_self";
  if (sameTab && anchor.href) {
    event.preventDefault();
    void Promise.resolve(track(label, id)).finally(() => {
      window.location.assign(anchor.href);
    });
    return;
  }
  void track(label, id);
}

function stripHtmlToText(html: string): string {
  return String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Canvas contentEditable HTML body for SchemaBlock text (and nested children). */
function SchemaCanvasHtml({
  path,
  html,
  className,
  style,
  placeholder,
}: {
  path: string;
  html: string;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
}) {
  const { get, set, editable, inline } = useEdit();
  const trackTextClick = usePublicSchemaClick("Text");
  const stored = get(path, html);
  const display = stored == null ? html : String(stored);
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = React.useState(false);
  const isEmpty = !stripHtmlToText(display);
  const canvasPlaceholderHtml = placeholder || "";
  const fieldUndo = useFieldTextUndo(path, display, focused);

  React.useEffect(() => {
    if (!ref.current || focused) return;
    if (document.activeElement === ref.current) return;
    if (isEmpty && canvasPlaceholderHtml) {
      ref.current.innerHTML = stripUnsafeHtml(canvasPlaceholderHtml);
      ref.current.setAttribute("data-canvas-placeholder", "true");
      return;
    }
    ref.current.removeAttribute("data-canvas-placeholder");
    ref.current.innerHTML = stripUnsafeHtml(display) || "";
  }, [display, focused, isEmpty, canvasPlaceholderHtml]);

  if (!editable || !inline || !path) {
    if (isEmpty) return null;
    return (
      <div
        className={className}
        style={style}
        onClick={(event) => schemaAnchorClick(event, trackTextClick)}
        dangerouslySetInnerHTML={{ __html: stripUnsafeHtml(display) }}
      />
    );
  }

  if (isEmpty && !focused && !canvasPlaceholderHtml) {
    return null;
  }

  const applyHistory = (snapshot: string | null) => {
    if (snapshot == null || !ref.current) return;
    ref.current.innerHTML = snapshot;
    ref.current.removeAttribute("data-canvas-placeholder");
  };
  const applyUndo = () => applyHistory(fieldUndo.undoOnce());
  const applyRedo = () => applyHistory(fieldUndo.redoOnce());

  return (
    <>
      <div
        ref={ref}
        className={`${className || ""} mte-schema-rich-editable`}
        style={style}
        data-om-path={path}
        data-om-edit
        data-editing={focused ? "true" : "false"}
        contentEditable
        suppressContentEditableWarning
        onFocus={() => {
          setFocused(true);
          if (ref.current?.getAttribute("data-canvas-placeholder") === "true") {
            ref.current.innerHTML = "";
            ref.current.removeAttribute("data-canvas-placeholder");
          }
        }}
        onInput={() => {
          if (!ref.current) return;
          fieldUndo.recordInput(ref.current.innerHTML);
        }}
        onBlur={() => {
          setFocused(false);
          const next = ref.current?.innerHTML || "";
          const nextText = stripHtmlToText(next);
          if (!nextText || isSchemaTextBodyPlaceholder(next)) {
            fieldUndo.markCommitted("");
            set(path, null);
            return;
          }
          if (next === html) set(path, null);
          else set(path, next);
          fieldUndo.markCommitted(next === html ? html : next);
        }}
        onKeyDown={(e) => {
          const historyKey = inlineHistoryShortcut(e);
          if (historyKey === "undo" && fieldUndo.canUndo) {
            e.preventDefault();
            e.stopPropagation();
            applyUndo();
          } else if (historyKey === "redo" && fieldUndo.canRedo) {
            e.preventDefault();
            e.stopPropagation();
            applyRedo();
          }
        }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      />
      <EditSessionUndoChip
        anchorRef={ref}
        visible={focused && (fieldUndo.canUndo || fieldUndo.canRedo)}
        canUndo={fieldUndo.canUndo}
        canRedo={fieldUndo.canRedo}
        layoutTick={fieldUndo.layoutTick}
        onUndo={applyUndo}
        onRedo={applyRedo}
      />
    </>
  );
}

function schemaEditPrefix(sectionPath: string | null, cfg?: SectionCfg, nested?: boolean) {
  if (!sectionPath) return "";
  const childId = String(cfg?.id || "").trim();
  if (nested && childId) return `section.${sectionPath}.child.${childId}.`;
  return `section.${sectionPath}.`;
}

function asItems(cfg?: SectionCfg): GalleryItem[] {
  const raw = cfg?.items;
  if (!Array.isArray(raw)) return [];
  return raw.filter((it) => it && typeof it === "object") as GalleryItem[];
}

/** Highlighted Add/Edit state: the section is on the page and still being filled. */
export function SchemaDraftEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div className="mte-schema-draft-empty" role="status">
      {children}
    </div>
  );
}

function GalleryArrowIcon({ dir }: { dir: "prev" | "next" }) {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d={dir === "prev" ? "M10 3.5 5.5 8 10 12.5" : "M6 3.5 10.5 8 6 12.5"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Image / Gallery layouts — shared by live canvas + add-drawer ghost preview. */
export function GalleryBlock({
  cfg,
}: {
  cfg?: SectionCfg;
}) {
  const sectionPath = useTemplateSectionInstance();
  const { editable, get } = useEdit();
  const trackGallery = usePublicSchemaClick("Gallery");
  const items = asItems(cfg);
  const layout = String(cfg?.layout || "single");
  // Match inspector: Show captions defaults on (`!== false`), not strict true.
  const showCaptions = cfg?.captions !== false;
  const [idx, setIdx] = React.useState(0);
  const [stripOverflows, setStripOverflows] = React.useState(false);
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(false);
  const stripRef = React.useRef<HTMLDivElement | null>(null);
  const marqueeRef = React.useRef<TemplateMarqueeTrackHandle | null>(null);
  const filled = items.filter((it) => String(it?.photo || "").trim());
  const source = filled.length ? filled : [{}];
  const safeIdx = source.length ? Math.min(idx, source.length - 1) : 0;
  const current = source[safeIdx];
  const photoKey = filled.map((it) => String(it.photo || "")).join("|");

  React.useEffect(() => {
    setIdx(0);
  }, [layout, source.length]);

  React.useLayoutEffect(() => {
    if (layout !== "carousel") return;
    const el = stripRef.current;
    if (!el) {
      setStripOverflows(false);
      setCanScrollPrev(false);
      setCanScrollNext(false);
      return;
    }
    const measure = () => {
      const overflows = galleryStripNeedsArrows(el);
      setStripOverflows((prev) => (prev === overflows ? prev : overflows));
      const edges = galleryStripScrollEdges(el);
      setCanScrollPrev((prev) => (prev === edges.canPrev ? prev : edges.canPrev));
      setCanScrollNext((prev) => (prev === edges.canNext ? prev : edges.canNext));
    };
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    const imgs = Array.from(el.querySelectorAll("img"));
    imgs.forEach((img) => img.addEventListener("load", measure));
    return () => {
      el.removeEventListener("scroll", measure);
      ro.disconnect();
      imgs.forEach((img) => img.removeEventListener("load", measure));
    };
  }, [layout, photoKey, cfg?.motion]);

  const scrollStrip = (dir: number) => {
    const el = stripRef.current;
    if (!el) return;
    const edges = galleryStripScrollEdges(el);
    if (dir < 0 && !edges.canPrev) return;
    if (dir > 0 && !edges.canNext) return;
    const cell = el.querySelector(".mte-schema-gal-cell") as HTMLElement | null;
    const gap = 10;
    const step = (cell?.offsetWidth || 160) + gap;
    // Clamp hard so arrows / snap never leave empty space past the last tile.
    const next = Math.min(edges.max, Math.max(0, el.scrollLeft + dir * step));
    el.scrollTo({ left: next, behavior: "smooth" });
  };

  const stepSingle = (dir: number) => {
    if (source.length < 2) return;
    setIdx((n) => (n + dir + source.length) % source.length);
  };

  const tile = (it: GalleryItem | undefined, key: string, wide?: boolean) => {
    const itemKey = String(it?.id || key);
    const capPath = sectionPath
      ? `section.${sectionPath}.items.${itemKey}.caption`
      : `gallery.${itemKey}.caption`;
    // Prefer live contentEdits so Show captions reflects inline edits immediately.
    const cap = String(get(capPath, it?.caption || "") || "").trim();
    return (
      <div key={key} className="mte-schema-gal-cell">
        {it?.photo ? (
          <img
            src={it.photo}
            alt={cap || it.caption || ""}
            className={`mte-tpl-img-filter mte-schema-gal-img${wide ? " is-wide" : ""}`}
            style={bannerImageFrameStyle(resolveBannerImageFrame(it), {
              objectFit: "cover",
            })}
          />
        ) : (
          <div className={`mte-schema-gal-ph${wide ? " is-wide" : ""}`} />
        )}
        {showCaptions && (cap || editable) ? (
          <div className="mte-schema-gal-cap">
            {editable ? (
              <E path={capPath} placeholder="Add a caption…">
                {cap}
              </E>
            ) : (
              cap
            )}
          </div>
        ) : null}
      </div>
    );
  };

  if (!filled.length) {
    if (!editable) return null;
    return (
      <div className="mte-schema-gal-ph is-wide mte-schema-gal-ph--empty">
        Upload an image
      </div>
    );
  }

  if (layout === "grid") {
    const cols = String(cfg?.cols || "3");
    return (
      <div
        className="mte-schema-gal-grid"
        style={
          {
            ["--mte-gal-cols" as string]: cols,
          } as React.CSSProperties
        }
      >
        {source.map((it, i) => tile(it, it.id || String(i)))}
      </div>
    );
  }

  if (layout === "carousel") {
    const motion = String(cfg?.motion || "static");
    // Marquee only — Auto-scroll was removed from Add/Edit (use Marquee instead).
    if (motion === "marquee" && source.length > 1) {
      const ticker = shouldMarqueeTicker(source.length);
      const loop = ticker ? source : [...source, ...source];
      // Same stage + arrows as Static so creators can still nudge left/right
      // while the marquee autoplays (and when paused / selected).
      return (
        <div className="mte-schema-gal-carousel">
          <div className="mte-schema-gal-stage">
            <button
              type="button"
              className="mte-schema-gal-arrow is-prev"
              aria-label="Previous"
              onClick={(e) => {
                e.stopPropagation();
                void trackGallery("Previous", "prev");
                marqueeRef.current?.nudge(1);
              }}
            >
              <GalleryArrowIcon dir="prev" />
            </button>
            <TemplateMarqueeTrack
              ref={marqueeRef}
              mode={ticker ? "ticker" : "loop"}
              outerClassName="mte-schema-gal-marquee"
              trackClassName="mte-schema-gal-marquee-track mte-schema-gal-marquee-track--js"
            >
              {loop.map((it, i) => tile(it, `${it.id || "img"}-${i}`))}
            </TemplateMarqueeTrack>
            <button
              type="button"
              className="mte-schema-gal-arrow is-next"
              aria-label="Next"
              onClick={(e) => {
                e.stopPropagation();
                void trackGallery("Next", "next");
                marqueeRef.current?.nudge(-1);
              }}
            >
              <GalleryArrowIcon dir="next" />
            </button>
          </div>
        </div>
      );
    }
    // Only when the strip actually overflows (e.g. phone peek). Two desktop
    // tiles that already fit must not show non-working arrows. At each end,
    // disable (don't hide) the arrow so reachability stays clear.
    const showArrows = stripOverflows;
    return (
      <div className="mte-schema-gal-carousel">
        <div className="mte-schema-gal-stage">
          {showArrows ? (
            <button
              type="button"
              className="mte-schema-gal-arrow is-prev"
              aria-label="Previous"
              disabled={!canScrollPrev}
              aria-disabled={!canScrollPrev}
              onClick={(e) => {
                e.stopPropagation();
                if (!canScrollPrev) return;
                void trackGallery("Previous", "prev");
                scrollStrip(-1);
              }}
            >
              <GalleryArrowIcon dir="prev" />
            </button>
          ) : null}
          <div ref={stripRef} className="mte-schema-gal-strip">
            {source.map((it, i) => tile(it, it.id || String(i)))}
          </div>
          {showArrows ? (
            <button
              type="button"
              className="mte-schema-gal-arrow is-next"
              aria-label="Next"
              disabled={!canScrollNext}
              aria-disabled={!canScrollNext}
              onClick={(e) => {
                e.stopPropagation();
                if (!canScrollNext) return;
                void trackGallery("Next", "next");
                scrollStrip(1);
              }}
            >
              <GalleryArrowIcon dir="next" />
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  // Single — one wide frame; arrows only when multiple images.
  return (
    <div className="mte-schema-gal-single">
      <div className="mte-schema-gal-single-frame">
        {tile(current, current?.id || "single", true)}
        {source.length > 1 ? (
          <>
            <button
              type="button"
              className="mte-schema-gal-arrow is-prev"
              aria-label="Previous"
              onClick={(e) => {
                e.stopPropagation();
                void trackGallery("Previous", "prev");
                stepSingle(-1);
              }}
            >
              <GalleryArrowIcon dir="prev" />
            </button>
            <button
              type="button"
              className="mte-schema-gal-arrow is-next"
              aria-label="Next"
              onClick={(e) => {
                e.stopPropagation();
                void trackGallery("Next", "next");
                stepSingle(1);
              }}
            >
              <GalleryArrowIcon dir="next" />
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}

/** Testimonials — grid / single / carousel (+ autoplay). Shared with ghost preview. */
export function TestimonialsBlock({
  cfg,
  editPrefix = "",
}: {
  cfg?: SectionCfg;
  /** When set (live editor), quote/name are canvas-editable. */
  editPrefix?: string;
}) {
  const { editable } = useEdit();
  const trackTestimonial = usePublicSchemaClick("Testimonials");
  const demos = SCHEMA_DEMO.testimonials.items;
  const list = asItems(cfg) as unknown as Record<string, string>[];
  const pristine =
    editable &&
    schemaItemsArePristine(list, SCHEMA_DEMO.testimonials.items, [
      "quote",
      "name",
    ]);
  const seed: SchemaRow[] = list.length
    ? list
    : pristine
      ? demos.map((_, i) => ({ id: `demo-${i}`, quote: "", name: "" }))
      : [];
  const source = seed
    .map((it, i): SchemaRow => {
      const demo = demos[i] || demos[0];
      const storedQuote = String(it.quote || "").trim();
      const storedName = String(it.name || "").trim();
      return {
        ...it,
        quote: schemaPreviewText(storedQuote, demo?.quote || "", pristine),
        name: schemaPreviewText(storedName, demo?.name || "", pristine),
        _demo: pristine && !storedQuote,
      };
    })
    .filter(
      (it) => String(it.quote || "").trim() || String(it.name || "").trim(),
    );
  const layout = String(cfg?.layout || "carousel");
  const motion = String(cfg?.motion || "").trim();
  // Creator Tools Drawers.html Motion: Static | Marquee (carousel only).
  const autoplay =
    motion === "marquee" || (motion !== "static" && cfg?.autoplay === true);
  // Design has no star toggle — off unless explicitly enabled on older blocks.
  const showStars = cfg?.stars === true;
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    setIdx(0);
  }, [layout, source.length]);

  // Single featured: cycle quotes when autoplay is on.
  React.useEffect(() => {
    if (layout !== "single" || !autoplay || source.length < 2) return;
    const tick = window.setInterval(() => {
      setIdx((n) => (n + 1) % source.length);
    }, 4000);
    return () => window.clearInterval(tick);
  }, [layout, autoplay, source.length]);

  if (!source.length) {
    if (!editable) return null;
    return (
      <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.testimonials}</SchemaDraftEmpty>
    );
  }

  const card = (
    it: SchemaRow,
    key: string,
    itemIndex: number,
  ) => {
    const itemKey = String(it.id || itemIndex);
    const quotePath = editPrefix ? `${editPrefix}items.${itemKey}.quote` : "";
    const namePath = editPrefix ? `${editPrefix}items.${itemKey}.name` : "";
    const quoteText = String(it.quote || "").replace(/^"|"$/g, "");
    const nameText = String(it.name || "").trim();
    const paintDemo = Boolean(it._demo);
    return (
      <div
        key={key}
        className="mte-schema-testimonial"
        onClick={() => {
          void trackTestimonial(nameText || quoteText || "Testimonial", itemKey);
        }}
      >
        {showStars ? <div className="mte-schema-stars">★★★★★</div> : null}
        {quoteText || quotePath ? (
          <p>
            {paintDemo || !quotePath ? (
              quoteText
            ) : (
              <E path={quotePath} as="span" placeholder="Add a testimonial…">
                {quoteText}
              </E>
            )}
          </p>
        ) : null}
        {nameText || namePath ? (
          <cite>
            {paintDemo || !namePath ? (
              nameText
            ) : (
              <E path={namePath} as="span" placeholder="Name">
                {nameText}
              </E>
            )}
          </cite>
        ) : null}
      </div>
    );
  };

  if (layout === "grid") {
    return (
      <div className="mte-schema-testimonials is-grid">
        {source.map((it, i) => card(it, it.id || String(i), i))}
      </div>
    );
  }

  if (layout === "single") {
    const safeIdx = source.length ? Math.min(idx, source.length - 1) : 0;
    const current = source[safeIdx];
    return (
      <div className="mte-schema-testimonials is-single">
        {card(current, current?.id || "single", safeIdx)}
      </div>
    );
  }

  // Carousel + autoplay → seamless left-origin loop (never ticker).
  // Ticker parks the first card on the right edge until it scrolls, which
  // reads as "stuck on the right" in the dashboard (esp. hover-paused).
  if (autoplay && source.length > 1 && !shouldMarqueeTicker(source.length)) {
    const loop = [...source, ...source];
    return (
      <TemplateMarqueeTrack
        mode="loop"
        outerClassName="mte-schema-testimonials-marquee"
        trackClassName="mte-schema-testimonials-marquee-track mte-schema-testimonials-marquee-track--js"
      >
        {loop.map((it, i) =>
          card(it, `${it.id || "t"}-${i}`, i % source.length),
        )}
      </TemplateMarqueeTrack>
    );
  }

  return (
    <div className="mte-schema-testimonials is-carousel">
      {source.map((it, i) => card(it, it.id || String(i), i))}
    </div>
  );
}

/** As Seen On logos — marquee / row / grid / stacked. Shared with ghost preview. */
export function AsSeenOnBlock({ cfg }: { cfg?: SectionCfg }) {
  const { editable, get } = useEdit();
  const sectionPath = useTemplateSectionInstance();
  const trackLogo = usePublicSchemaClick("As Seen On");
  const demos = SCHEMA_DEMO.asseenon.items;
  const list = asItems(cfg) as unknown as Record<string, string>[];
  const pristine =
    editable &&
    schemaItemsArePristine(list, SCHEMA_DEMO.asseenon.items, ["name"]) &&
    !list.some((it) => String(it.photo || "").trim());
  const seed: SchemaRow[] = list.length
    ? list
    : pristine
      ? demos.map((_, i) => ({ id: `demo-${i}`, name: "", photo: "" }))
      : [];
  const source = seed
    .map((it, i): SchemaRow => {
      const demo = demos[i] || demos[0];
      const storedName = String(it.name || "").trim();
      return {
        ...it,
        name: schemaPreviewText(storedName, demo?.name || "", pristine),
      };
    })
    .filter(
      (it) => String(it.name || "").trim() || String(it.photo || "").trim(),
    );
  if (!source.length) {
    if (!editable) return null;
    return <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.asseenon}</SchemaDraftEmpty>;
  }

  const layout = String(cfg?.layout || "marquee");
  // Creator Tools Drawers.html — Auto-scroll off by default.
  const autoplay = cfg?.autoplay === true;

  const logoCountStyle = {
    ["--mte-logo-count" as string]: String(Math.max(1, source.length)),
  } as React.CSSProperties;

  const logo = (it: SchemaRow, key: string) => {
    const itemKey = String(it.id || key);
    const path = sectionPath
      ? `section.${sectionPath}.items.${itemKey}`
      : `asseenon.${itemKey}`;
    const photo = String(it.photo || "").trim();
    const nameFallback = String(it.name || "").trim();
    const name = String(
      get(`${path}.text`, nameFallback) || nameFallback,
    ).trim();
    const href = String(
      get(`${path}.href`, String(it.url || "").trim()) ||
        String(it.url || "").trim(),
    ).trim();
    const hasLink =
      Boolean(href) &&
      href !== "#" &&
      href !== "https://" &&
      href !== "http://";

    if (!photo && !name) return null;

    // Public / locked: real link so logos redirect.
    if (!editable) {
      const inner = photo ? (
        <img src={photo} alt={name || ""} />
      ) : (
        name
      );
      if (hasLink) {
        return (
          <a
            key={key}
            className="mte-schema-logo"
            href={href.startsWith("http") || href.startsWith("//") || href.startsWith("/") || href.startsWith("mailto:") || href.startsWith("tel:")
              ? href
              : `https://${href.replace(/^\/+/, "")}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              schemaAnchorClick(e, trackLogo);
              if (openTemplateButtonHref(href)) {
                e.preventDefault();
              }
            }}
          >
            {inner}
          </a>
        );
      }
      return (
        <span key={key} className="mte-schema-logo">
          {inner}
        </span>
      );
    }

    // Editor: same Visit / Change text / Change link menu as other linked CTAs.
    if (photo) {
      return (
        <span key={key} className="mte-schema-logo mte-schema-logo--has-photo">
          <img src={photo} alt="" aria-hidden />
          <EL
            path={path}
            href={href || "#"}
            as="span"
            lockText
            className="mte-schema-logo-el"
          >
            {name}
          </EL>
        </span>
      );
    }

    return (
      <EL
        key={key}
        path={path}
        href={href || "#"}
        as="span"
        className="mte-schema-logo"
      >
        {name}
      </EL>
    );
  };

  if (layout === "grid") {
    return (
      <div className="mte-schema-logos is-grid">
        {source.map((it, i) => logo(it, it.id || String(i)))}
      </div>
    );
  }

  if (layout === "stacked") {
    return (
      <div className="mte-schema-logos is-stacked">
        {source.map((it, i) => logo(it, it.id || String(i)))}
      </div>
    );
  }

  if (layout === "row") {
    return (
      <div className="mte-schema-logos is-row">
        {source.map((it, i) => logo(it, it.id || String(i)))}
      </div>
    );
  }

  // Marquee: autoplay → looping track; otherwise a static horizontal strip.
  if (autoplay && source.length > 0) {
    const ticker = shouldMarqueeTicker(source.length);
    const loop = ticker ? source : [...source, ...source];
    return (
      <TemplateMarqueeTrack
        mode={ticker ? "ticker" : "loop"}
        outerClassName="mte-schema-logos-marquee"
        outerStyle={logoCountStyle}
        trackClassName="mte-schema-logos-marquee-track mte-schema-logos-marquee-track--js"
      >
        {loop.map((it, i) => logo(it, `${it.id || "logo"}-${i}`))}
      </TemplateMarqueeTrack>
    );
  }

  return (
    <div className="mte-schema-logos is-marquee" style={logoCountStyle}>
      {source.map((it, i) => logo(it, it.id || String(i)))}
    </div>
  );
}

/** Map — inset / full-width with pin marker. Shared with ghost preview. */
export function MapBlock({ cfg }: { cfg?: SectionCfg }) {
  const { editable } = useEdit();
  const sectionPath = useTemplateSectionInstance();
  const storedAddress = String(cfg?.address || "").trim();
  const storedLabel = String(cfg?.label || "").trim();
  const address = schemaDrawerText(storedAddress, SCHEMA_DEMO.map.address);
  const label = schemaDrawerText(storedLabel, SCHEMA_DEMO.map.label);
  if (!address && !label) {
    if (!editable) return null;
    return <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.map}</SchemaDraftEmpty>;
  }
  const q = encodeURIComponent(address || label || "");
  const full = String(cfg?.layout || "") === "full";
  const showPinLabel = cfg?.pinlabel !== false && Boolean(label);
  const showAddress = cfg?.addresstext !== false && Boolean(address);
  const showDirections = cfg?.directions !== false && Boolean(q);
  const mapClass = `mte-schema-map${full ? " is-full" : " is-inset"}${
    q ? "" : " is-empty"
  }`;
  const labelPath = sectionPath
    ? `section.${sectionPath}.label`
    : "map.label";
  const addressPath = sectionPath
    ? `section.${sectionPath}.address`
    : "map.address";
  const paintDemo =
    editable &&
    isSchemaDemoText(storedAddress, SCHEMA_DEMO.map.address) &&
    isSchemaDemoText(storedLabel, SCHEMA_DEMO.map.label);

  const marker = (
    <div className="mte-schema-map-marker" aria-hidden={!showPinLabel}>
      {showPinLabel ? (
        <div className="mte-schema-map-pin">
          {paintDemo ? label : <E path={labelPath}>{label}</E>}
        </div>
      ) : null}
      <span className="mte-schema-map-drop" />
    </div>
  );

  return (
    <>
      <div className={mapClass}>
        {q ? (
          <iframe
            title={label || address || "Map"}
            src={`https://maps.google.com/maps?q=${q}&z=12&output=embed`}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : null}
        {marker}
      </div>
      {showAddress ? (
        <div className="mte-schema-map-addr">
          {paintDemo ? address : <E path={addressPath}>{address}</E>}
        </div>
      ) : null}
      {showDirections ? (
        editable ? (
          <span className="mte-schema-map-dir">Get directions</span>
        ) : (
          <a
            className="mte-schema-map-dir"
            href={`https://maps.google.com/maps?q=${q}`}
            target="_blank"
            rel="noreferrer"
          >
            Get directions
          </a>
        )
      ) : null}
    </>
  );
}

function AccordionBlock({
  T,
  cfg,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
}) {
  const { editable } = useEdit();
  const sectionPath = useTemplateSectionInstance();
  const trackAccordion = usePublicSchemaClick("Accordion");
  const items = asItems(cfg) as unknown as Record<string, string>[];
  const rawTitle = String(cfg?.heading || cfg?.title || "").trim();
  const demos = SCHEMA_DEMO.accordion.items;
  const pristine =
    editable &&
    schemaItemsArePristine(items, SCHEMA_DEMO.accordion.items, ["q", "a"]);
  const title = rawTitle;
  const expandFirst = cfg?.expand === true;
  const [open, setOpen] = React.useState(expandFirst ? 0 : -1);
  React.useEffect(() => {
    setOpen(expandFirst ? 0 : -1);
  }, [expandFirst]);
  const seed: SchemaRow[] = items.length
    ? items
    : pristine
      ? demos.map((_, i) => ({ id: `demo-${i}`, q: "", a: "" }))
      : [];
  const list = seed.map((it, i): SchemaRow => {
    const demo = demos[i] || demos[0];
    const storedQ = String(it.q || "").trim();
    const storedA = String(it.a || "").trim();
    return {
      ...it,
      q: schemaPreviewText(storedQ, demo?.q || "", pristine),
      a: schemaPreviewText(storedA, demo?.a || "", pristine),
      _demo: pristine && !storedQ,
    };
  }).filter((it) => String(it.q || "").trim() || String(it.a || "").trim());
  if (!title && !list.length) {
    if (!editable) return null;
    return (
      <Shell T={T} cfg={shellCfgWithoutHeading(cfg)}>
        <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.accordion}</SchemaDraftEmpty>
      </Shell>
    );
  }
  const titlePath = sectionPath
    ? `section.${sectionPath}.heading`
    : "faq.heading";
  return (
    <Shell T={T} cfg={shellCfgWithoutHeading(cfg)}>
      <div className="mte-schema-faq">
        {title || editable ? (
          <h3 className="mte-schema-faq-title">
            {pristine && title ? (
              title
            ) : (
              <E path={titlePath} placeholder={SCHEMA_DEMO.accordion.heading}>
                {title}
              </E>
            )}
          </h3>
        ) : null}
        <div className="mte-schema-faq-list">
          {list.map((it, i) => {
            const isOpen = open === i;
            const question = String(it.q || "").trim();
            const answer = String(it.a || "").trim();
            const itemKey = String(it.id || i);
            const qPath = sectionPath
              ? `section.${sectionPath}.items.${itemKey}.q`
              : `faq.items.${itemKey}.q`;
            const aPath = sectionPath
              ? `section.${sectionPath}.items.${itemKey}.a`
              : `faq.items.${itemKey}.a`;
            if (!editable && !question && !answer) return null;
            const paintDemo = Boolean(it._demo);
            const toggleOpen = () => {
              void trackAccordion(question || `Item ${i + 1}`, itemKey);
              setOpen(isOpen ? -1 : i);
            };
            return (
              <div key={itemKey} className="mte-schema-faq-item">
                {/*
                  Expand control stays a <button>; question text / inline E sit
                  beside it so contentEditable is not nested inside the button.
                */}
                <div
                  className="mte-schema-faq-head"
                  onClick={(e) => {
                    if (
                      (e.target as HTMLElement | null)?.closest?.(
                        "[data-om-edit]",
                      )
                    ) {
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();
                    toggleOpen();
                  }}
                >
                  <div className="mte-schema-faq-q">
                    {paintDemo ? (
                      question
                    ) : (
                      <E path={qPath} placeholder={`Question ${i + 1}`}>
                        {question}
                      </E>
                    )}
                  </div>
                  <button
                    type="button"
                    className="mte-schema-faq-toggle"
                    aria-expanded={isOpen}
                    aria-label={isOpen ? "Collapse answer" : "Expand answer"}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleOpen();
                    }}
                  >
                    <span aria-hidden>{isOpen ? "−" : "+"}</span>
                  </button>
                </div>
                {isOpen && (answer || editable) ? (
                  <p className="mte-schema-faq-answer">
                    {paintDemo ? (
                      answer
                    ) : (
                      <E path={aPath} multiline placeholder="Add an answer…">
                        {answer}
                      </E>
                    )}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </Shell>
  );
}

function shellCfgWithoutHeading(cfg?: SectionCfg): SectionCfg | undefined {
  if (!cfg) return cfg;
  const next = { ...cfg };
  delete next.heading;
  return next;
}

/** Nested children inherit parent Background unless they set their own tone. */
function schemaChildInheritsParentBg(cfg?: SectionCfg) {
  const tone = String(cfg?.bgTone || "bg");
  return tone === "bg";
}

function SchemaShell({
  T,
  cfg,
  nested,
  fullBleed,
  children,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
  nested?: boolean;
  fullBleed?: boolean;
  children: React.ReactNode;
}) {
  if (nested && schemaChildInheritsParentBg(cfg)) {
    return <div className="mte-schema-child">{children}</div>;
  }
  if (nested) {
    return (
      <div className="mte-schema-child">
        <Shell T={T} cfg={shellCfgWithoutHeading(cfg)} fullBleed={fullBleed}>
          {children}
        </Shell>
      </div>
    );
  }
  return (
    <Shell T={T} cfg={shellCfgWithoutHeading(cfg)} fullBleed={fullBleed}>
      {children}
    </Shell>
  );
}

function textAlign(layout: string): React.CSSProperties["textAlign"] {
  if (layout === "center" || layout === "right") return layout;
  return "left";
}

function youtubeId(url: string): string | null {
  try {
    const u = new URL(url);
    if (/(^|\.)youtu\.be$/i.test(u.hostname)) {
      const id = u.pathname.split("/").filter(Boolean)[0];
      return id || null;
    }
    if (/(^|\.)youtube\.com$/i.test(u.hostname)) {
      const v = u.searchParams.get("v");
      if (v) return v;
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts[0] === "embed" || parts[0] === "shorts") return parts[1] || null;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function vimeoId(url: string): string | null {
  const m = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
  return m?.[1] || null;
}

/** Keep Flodesk (and similar) form-kit scripts; drop everything else (XSS). */
function isAllowedEmbedScriptTag(scriptTag: string): boolean {
  const srcMatch = scriptTag.match(
    /\bsrc\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i,
  );
  const src = (srcMatch?.[2] || srcMatch?.[3] || srcMatch?.[4] || "").trim();
  if (src) {
    try {
      const url = new URL(src, "https://assets.flodesk.com");
      const host = url.hostname.toLowerCase();
      return (
        host === "assets.flodesk.com" ||
        host === "form.flodesk.com" ||
        host.endsWith(".flodesk.com")
      );
    } catch {
      return false;
    }
  }
  const body = scriptTag
    .replace(/^<script\b[^>]*>/i, "")
    .replace(/<\/script>$/i, "");
  // Flodesk universal bootstrap loader
  if (
    /assets\.flodesk\.com/i.test(body) &&
    /FlodeskObject|flodesk/i.test(body)
  ) {
    return true;
  }
  // Flodesk form:handle init
  if (/\bfd\s*\(\s*['"]form:handle['"]/i.test(body)) {
    return true;
  }
  return false;
}

export function sanitizeEmbedHtml(html: string): string {
  return String(html || "")
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, (block) =>
      isAllowedEmbedScriptTag(block) ? block : "",
    )
    .replace(/\son\w+\s*=\s*("[^"]*"|'[^']*')/gi, "")
    // Document shells break the editor when injected into the live canvas.
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<\/?(?:html|head|body)\b[^>]*>/gi, "")
    .trim();
}

/**
 * Render pasted embed HTML inside a sandboxed iframe so tags like
 * `</body></html>` cannot close or restyle the website editor document
 * (which clips the inspector and kills scroll).
 */
/** YouTube / Vimeo stay locked to 16:9; other URL embeds can grow. */
export function isFixedAspectVideoEmbed(src: string): boolean {
  return Boolean(youtubeId(src) || vimeoId(src));
}

export function EmbedHtmlFrame({
  html = "",
  src,
  title = "Embed",
  className,
  style,
  allow = "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture",
  referrerPolicy,
  /** Full-bleed URL embeds (non-video): fill the viewport instead of 16:9 clip. */
  viewportFit = false,
}: {
  html?: string;
  /** Direct embed URL. Omit to render pasted HTML in a sandboxed srcdoc. */
  src?: string;
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  allow?: string;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  viewportFit?: boolean;
}) {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const safe = sanitizeEmbedHtml(html);
  // Pasted HTML (Flodesk forms, etc.): grow with content instead of 16:9 clip.
  const autoHeight = Boolean(!src && safe);
  const isFormEmbed = Boolean(safe && /<form\b/i.test(safe));
  const [iframeHeight, setIframeHeight] = React.useState(0);

  React.useEffect(() => {
    if (!src) return;
    // Self-sizing /component/ embeds + viewport-fit site URLs may post SB_EMBED_SIZE.
    if (!viewportFit && !isSelfSizingEmbedHtml(src)) return;
    return listenForSbEmbedSize(rootRef.current);
  }, [src, viewportFit]);

  React.useEffect(() => {
    if (!autoHeight || !iframeRef.current) return;
    return attachSrcDocEmbedHeight(iframeRef.current, setIframeHeight, {
      formEmbed: isFormEmbed,
    });
  }, [autoHeight, safe, isFormEmbed]);

  // Never use <base target="_blank"> for forms — that forced Flodesk's native
  // POST into a new tab (captcha / redirect) while leaving the filled form behind.
  const baseTag = isFormEmbed
    ? ""
    : `<base target="_blank" rel="noopener"/>`;

  // Fixed 16:9 shells still need overflow scroll; auto-height must not.
  const srcDoc = autoHeight
    ? buildEmbedSrcDoc(safe, { formEmbed: isFormEmbed })
    : `<!DOCTYPE html><html><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width,initial-scale=1"/>${baseTag}<style>html,body{margin:0;padding:0;width:100%;height:100%;overflow:auto;background:transparent}img,video,iframe,embed,object{max-width:100%;border:0}</style></head><body>${safe}</body></html>`;

  const sized =
    autoHeight && iframeHeight > 0
      ? ({ height: iframeHeight } as React.CSSProperties)
      : viewportFit
        ? ({ height: "100svh", minHeight: 480 } as React.CSSProperties)
        : undefined;

  // Form kits need scripts + same-origin measure; user-activation top nav lets
  // Flodesk's configured redirect leave the embed cleanly after submit.
  const sandbox = isFormEmbed
    ? "allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-same-origin allow-top-navigation-by-user-activation"
    : "allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms allow-same-origin";

  return (
    <div ref={rootRef} className={className} style={style}>
      {src ? (
        <iframe
          title={title}
          src={src}
          allow={allow}
          allowFullScreen
          referrerPolicy={referrerPolicy}
          style={sized}
        />
      ) : (
        <iframe
          ref={iframeRef}
          title={title}
          srcDoc={srcDoc}
          sandbox={sandbox}
          referrerPolicy={isFormEmbed ? "strict-origin-when-cross-origin" : "no-referrer"}
          loading="lazy"
          style={sized}
        />
      )}
    </div>
  );
}

/**
 * Podcast / audio players are short (~152px). Forcing 16:9 leaves a large empty
 * band under Spotify / Apple Podcasts embeds.
 */
export function isCompactAudioEmbed(raw: string): boolean {
  const s = String(raw || "").toLowerCase();
  if (!s) return false;
  return /spotify\.com|podcasts\.apple\.com|music\.amazon\.|soundcloud\.com|anchor\.fm|transistor\.fm|buzzsprout\.com|simplecast\.com|megaphone\.fm|overcast\.fm|pocketcasts|podbean|iheart\.com\/podcast|audioboom|libsyn|spreaker|castbox|podigee|captivate\.fm/.test(
    s,
  );
}

export function parseEmbed(raw: string): { kind: "empty" } | { kind: "html"; html: string } | { kind: "src"; src: string } {
  const s = raw.trim();
  if (!s) return { kind: "empty" };

  const asSrc = (candidate: string) => {
    const yt = youtubeId(candidate);
    if (yt) return { kind: "src" as const, src: `https://www.youtube.com/embed/${yt}` };
    const vim = vimeoId(candidate);
    if (vim) return { kind: "src" as const, src: `https://player.vimeo.com/video/${vim}` };
    try {
      const u = new URL(candidate);
      if (u.protocol === "http:" || u.protocol === "https:") {
        return { kind: "src" as const, src: candidate };
      }
    } catch {
      /* ignore */
    }
    return null;
  };

  const asHtml = (candidate: string) => {
    const html = sanitizeEmbedHtml(candidate);
    if (!html) return { kind: "empty" as const };
    return { kind: "html" as const, html };
  };

  // Pasted <iframe …> — pull the src so Inset / Full-width can size it.
  if (s.startsWith("<")) {
    const srcMatch = s.match(/\bsrc\s*=\s*("([^"]+)"|'([^']+)'|([^\s>]+))/i);
    const src = (srcMatch?.[2] || srcMatch?.[3] || srcMatch?.[4] || "").trim();
    if (src) {
      const decoded = src.replace(/&amp;/g, "&");
      const fromSrc = asSrc(decoded);
      if (fromSrc) return fromSrc;
    }
    return asHtml(s);
  }

  try {
    const u = new URL(s);
    if (u.protocol !== "http:" && u.protocol !== "https:") {
      return asHtml(s);
    }
    return asSrc(s) || { kind: "src", src: s };
  } catch {
    return asHtml(s);
  }
}

function colCount(layout: string) {
  if (layout === "col1") return 1;
  if (layout === "col3") return 3;
  if (layout === "col4") return 4;
  return 2;
}

type ColumnSlot = { id?: string; children?: SectionCfg[] };

/** Content Section layouts — shared by live canvas + add-drawer ghost preview. */
export function ColumnsBlock({
  cfg,
  showEmpty = false,
  renderChild,
}: {
  cfg?: SectionCfg;
  showEmpty?: boolean;
  renderChild?: (child: SectionCfg, index: number) => React.ReactNode;
}) {
  const n = colCount(String(cfg?.layout || "col2"));
  const rawColumns = cfg?.columns;
  const cols = (Array.isArray(rawColumns) ? rawColumns : []) as ColumnSlot[];
  const headerAlign = String(cfg?.headerAlign || "left");
  const heading = String(cfg?.heading || cfg?.title || "").trim();
  const headerText = Boolean(
    heading ||
      String(cfg?.eyebrow || "").trim() ||
      String(cfg?.sub || "").trim(),
  );
  const hasKids = cols.some(
    (col) => Array.isArray(col.children) && col.children.length > 0,
  );
  if (showEmpty && !hasKids && !(cfg?.showHeader === true && headerText)) {
    return <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.columns}</SchemaDraftEmpty>;
  }

  return (
    <>
      {cfg?.showHeader === true ? (
        <div
          className="mte-schema-sec-head"
          style={{ textAlign: textAlign(headerAlign) }}
        >
          {String(cfg?.eyebrow || "").trim() ? (
            <div className="mte-schema-sec-kicker">
              {String(cfg.eyebrow).toUpperCase()}
            </div>
          ) : null}
          {heading ? (
            <h2 className="mte-schema-sec-headline">{heading}</h2>
          ) : null}
          {String(cfg?.sub || "").trim() ? (
            <p className="mte-tpl-muted-p">{String(cfg.sub)}</p>
          ) : null}
        </div>
      ) : null}
      <div className="mte-schema-cols">
        {Array.from({ length: n }).map((_, i) => {
          const kids = cols[i]?.children || [];
          return (
            <div
              key={cols[i]?.id || i}
              className={`mte-schema-col${
                cfg?.dividers !== false && i > 0 ? " has-div" : ""
              }`}
            >
              {kids.length
                ? kids.map((child, ci) =>
                    renderChild ? (
                      <React.Fragment key={String(child.id || ci)}>
                        {renderChild(child, ci)}
                      </React.Fragment>
                    ) : (
                      <div
                        key={String(child.id || ci)}
                        className="mte-schema-slot-note"
                      >
                        {extraSectionLabelForCatalog(
                          String(child.blockType || child.catalogType || ""),
                        )}
                      </div>
                    ),
                  )
                : null}
            </div>
          );
        })}
      </div>
    </>
  );
}

function countdownText(expires: string): string | null {
  const endDate = parseDiscountExpiryDate(expires);
  if (!endDate) return null;
  const days = Math.ceil((endDate.getTime() - Date.now()) / 86400000);
  if (days < 0) return "expired";
  if (days === 0) return "Ends today";
  return `${days} day${days === 1 ? "" : "s"} left`;
}

/** Match site-wide `mte-tpl-btn` chrome; keep text layout as a plain link. */
function schemaCtaClass(layout: string, size: string, wide: boolean) {
  if (layout === "text") {
    const sizeCls =
      size === "s" ? " is-s" : size === "l" ? " is-l" : " is-m";
    return `mte-schema-cta-btn is-text${sizeCls}`;
  }
  const variant = layout === "outline" ? "ghost" : "primary";
  const sizeCls =
    size === "s"
      ? " mte-tpl-btn--size-s"
      : size === "l"
        ? " mte-tpl-btn--size-l"
        : "";
  const wideCls = wide ? " mte-tpl-btn--full-width" : "";
  return `${btnClass(variant)}${sizeCls}${wideCls}`;
}

function CtaBlock({
  T,
  cfg,
  nested,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
  nested?: boolean;
}) {
  const { editable } = useEdit();
  const trackTabsClick = React.useContext(TabsClickCtx);
  const trackButton = usePublicSchemaClick("Button");
  const label = String(
    cfg?.label || cfg?.heading || SCHEMA_CTA_LABEL_DEFAULT,
  );
  const href = String(cfg?.url || cfg?.ctaLink || "").trim();
  const hasLink = schemaCtaHasLink(href);
  const layout = String(cfg?.layout || "filled");
  const size = String(cfg?.size || "m");
  const wide = cfg?.wide === true;
  const className = schemaCtaClass(layout, size, wide);
  const openNewTab = cfg?.newtab !== false;
  const inner =
    hasLink && !editable ? (
      <div className="mte-schema-cta-wrap">
        <a
          className={className}
          href={href}
          target={openNewTab ? "_blank" : undefined}
          rel={openNewTab ? "noreferrer" : undefined}
          onClick={(event) => {
            const track = trackTabsClick ?? trackButton;
            const id = String(cfg?.id || href || label);
            if (!openNewTab) {
              event.preventDefault();
              void Promise.resolve(track(label, id)).finally(() => {
                window.location.assign(href);
              });
              return;
            }
            void track(label, id);
          }}
        >
          {label}
        </a>
      </div>
    ) : (
      <div className="mte-schema-cta-wrap">
        <span className={className}>{label}</span>
      </div>
    );
  return (
    <SchemaShell T={T} cfg={cfg} nested={nested}>
      {inner}
    </SchemaShell>
  );
}

/** Ribbon message strip — Static shows all messages; Scrolling always marquees. */
export function RibbonContent({
  cfg,
  editable = false,
}: {
  cfg?: SectionCfg;
  editable?: boolean;
}) {
  const sectionPath = useTemplateSectionInstance();
  const { get } = useEdit();
  const trackRibbon = usePublicSchemaClick("Ribbon");
  const items = asItems(cfg) as unknown as Record<string, string>[];
  const rawMessages: SchemaRow[] = items.length
    ? items
    : String(cfg?.heading || "").trim()
      ? [{ message: String(cfg?.heading) }]
      : [];
  const rawLayout = String(cfg?.layout || "static").toLowerCase();
  const isMarquee =
    rawLayout === "marquee" ||
    rawLayout === "scrolling" ||
    rawLayout === "scroll";
  const showIcon = cfg?.icon === true;
  const demos = SCHEMA_DEMO.ribbon.items;

  const itemPaths = (it: SchemaRow, key: string) => {
    const itemKey = String(it.id || key);
    return {
      messagePath: sectionPath
        ? `section.${sectionPath}.items.${itemKey}.message`
        : `ribbon.${itemKey}.message`,
      ctaPath: sectionPath
        ? `section.${sectionPath}.items.${itemKey}.ctaLabel`
        : `ribbon.${itemKey}.ctaLabel`,
    };
  };

  // Demos only while every slot is still empty. Once the creator types into
  // any message, preview shows live values only (no sibling factory demos).
  const pristine =
    editable &&
    schemaItemsArePristine(rawMessages, SCHEMA_DEMO.ribbon.items, ["message"]);
  const messages = (rawMessages.length
    ? rawMessages
    : pristine
      ? demos.map((_, i) => ({
          id: `demo-${i}`,
          message: "",
          ctaLabel: "",
        }))
      : []
  ).flatMap((it, i) => {
    const key = String(it.id || i);
    const { messagePath, ctaPath } = itemPaths(it, key);
    const storedMessage = String(
      get(messagePath, String(it.message || "")) || "",
    ).trim();
    const storedCta = String(
      get(ctaPath, String(it.ctaLabel || "")) || "",
    ).trim();
    const demo = demos[i] || demos[0];
    const message = schemaPreviewText(
      storedMessage,
      demo?.message || "",
      pristine,
    );
    const cta = schemaPreviewText(
      storedCta,
      !storedMessage ? demo?.ctaLabel || "" : "",
      pristine && !storedMessage,
    );
    if (!message && !cta) return [];
    const row: SchemaRow = {
      ...it,
      id: key,
      message,
      ctaLabel: cta,
      _demo: pristine && !storedMessage,
    };
    return [row];
  });

  if (!messages.length) {
    if (!editable) return null;
    return <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.ribbon}</SchemaDraftEmpty>;
  }

  const msg = (it: SchemaRow, key: string) => {
    const href = String(it.url || "").trim();
    const { messagePath, ctaPath } = itemPaths(it, key);
    const message = String(it.message || "").trim();
    const cta = String(it.ctaLabel || "").trim();
    if (!message && !cta) return null;
    const paintDemo = Boolean(it._demo);
    return (
      <span key={key} className="mte-schema-ribbon-msg">
        {showIcon ? <span aria-hidden>📣</span> : null}
        {message ? (
          <span>
            {paintDemo ? (
              message
            ) : (
              <E path={messagePath} placeholder="">
                {message}
              </E>
            )}
          </span>
        ) : null}
        {cta ? (
          href && !editable ? (
            <a
              className="mte-schema-ribbon-cta"
              href={href}
              onClick={(event) => schemaAnchorClick(event, trackRibbon)}
            >
              {cta}
            </a>
          ) : (
            <span className="mte-schema-ribbon-cta">
              {paintDemo ? (
                cta
              ) : (
                <E path={ctaPath} placeholder="">
                  {cta}
                </E>
              )}
            </span>
          )
        ) : null}
      </span>
    );
  };

  if (isMarquee) {
    // Two cycle wrappers (editable + plain duplicate) so loop distance is
    // measured between equal halves — not a viewport-sized ticker blank, and
    // not odd interleaved dots that break scrollWidth/2.
    const cycle = (editableCopy: boolean, copyKey: string) =>
      messages.map((it, i) => (
        <React.Fragment key={`${copyKey}-${it.id || i}`}>
          {i > 0 ? (
            <span className="mte-schema-ribbon-dot" aria-hidden>
              •
            </span>
          ) : null}
          {editableCopy ? (
            msg(it, it.id || String(i))
          ) : (
            <span className="mte-schema-ribbon-msg" aria-hidden>
              {showIcon ? <span aria-hidden>📣</span> : null}
              {String(it.message || "").trim() ? (
                <span>{String(it.message || "").trim()}</span>
              ) : null}
              {String(it.ctaLabel || "").trim() ? (
                <span className="mte-schema-ribbon-cta">
                  {String(it.ctaLabel || "").trim()}
                </span>
              ) : null}
            </span>
          )}
        </React.Fragment>
      ));
    return (
      <TemplateMarqueeTrack
        mode="loop"
        key={messages
          .map((it, i) => `${it.id || i}:${it.message || ""}:${it.ctaLabel || ""}`)
          .join("|")}
        outerClassName="mte-schema-ribbon-marquee"
        trackClassName="mte-schema-ribbon-track mte-schema-ribbon-track--js"
      >
        <span className="mte-schema-ribbon-cycle">{cycle(true, "a")}</span>
        <span className="mte-schema-ribbon-cycle" aria-hidden>
          {cycle(false, "b")}
        </span>
      </TemplateMarqueeTrack>
    );
  }

  const cols = Math.min(Math.max(messages.length, 1), 4);
  return (
    <div
      className="mte-schema-ribbon-static is-all"
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }}
    >
      {messages.map((it, i) => (
        <div key={it.id || i} className="mte-schema-ribbon-static-cell">
          {msg(it, it.id || String(i))}
        </div>
      ))}
    </div>
  );
}

function RibbonBlock({
  T,
  cfg,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
}) {
  const { editable } = useEdit();
  const dismissible = cfg?.dismissible !== false;
  const bgTone = (cfg?.bgTone as "bg" | "surface" | "accent" | undefined) || "bg";
  const ribbonBg =
    bgTone === "surface" ? T.surface : bgTone === "accent" ? T.accent : T.bg;
  const ribbonFg = bgTone === "accent" ? T.accentInk : T.ink;
  const [dismissed, setDismissed] = React.useState(false);

  if (dismissed && !editable) return null;

  return (
    <Shell T={T} cfg={{ ...cfg, bgTone }} fullBleed>
      <div
        className="mte-schema-ribbon"
        style={{ background: ribbonBg, color: ribbonFg }}
      >
        <RibbonContent cfg={cfg} editable={editable} />
        {dismissible && !editable ? (
          <button
            type="button"
            className="mte-schema-ribbon-x"
            aria-label="Dismiss"
            onClick={() => setDismissed(true)}
          >
            ✕
          </button>
        ) : null}
      </div>
    </Shell>
  );
}

function clampTabIndex(raw: unknown, tabCount: number): number {
  if (tabCount <= 0) return 0;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.min(Math.floor(n), tabCount - 1);
}

function TabsBlock({
  T,
  cfg,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
}) {
  const { editable } = useEdit();
  const templateAnalytics = useTemplateAnalyticsOptional();
  const sectionPath = useTemplateSectionInstance();
  const sectionId =
    String(sectionPath || "").split("/").pop() || String(cfg?.id || "tabs");
  const trackTabsClick = React.useCallback(
    (label: string, id: string) => {
      if (editable) return;
      const name = String(label || "").trim();
      if (!name) return;
      templateAnalytics?.trackSectionEvent(TEMPLATE_ANALYTICS_EVENTS.TABS_CLICK, {
        sectionId,
        sectionType: "Tabs",
        entityType: "section",
        entityId: id,
        entityLabel: name,
      });
    },
    [editable, sectionId, templateAnalytics],
  );
  const rawTabs = cfg?.tabs;
  const tabs = (Array.isArray(rawTabs) ? rawTabs : []) as {
    id?: string;
    label?: string;
    children?: SectionCfg[];
  }[];
  // Public / published: always land on the first tab. `cfg.activeTab` is only
  // editor navigation state (inspector focus / nested add) and must not become
  // the visitor-facing default.
  const cfgIndex = clampTabIndex(cfg?.activeTab, tabs.length);
  const [active, setActive] = React.useState(() => (editable ? cfgIndex : 0));
  React.useEffect(() => {
    if (!editable) return;
    setActive(cfgIndex);
  }, [editable, cfgIndex]);
  const safe = tabs.length ? Math.min(active, tabs.length - 1) : 0;
  const current = tabs[safe];
  const kids = current?.children || [];
  return (
    <TabsClickCtx.Provider value={trackTabsClick}>
      <Shell T={T} cfg={shellCfgWithoutHeading(cfg)}>
        <div className="mte-schema-tab-strip">
          {tabs.length ? (
            tabs.map((t, i) => (
              <button
                key={t.id || i}
                type="button"
                className={`mte-schema-tab-chip${i === safe ? " is-on" : ""}`}
                onClick={() => {
                  if (i !== safe) {
                    const label = isSchemaTabLabelPlaceholder(t.label)
                      ? ""
                      : String(t.label || "").trim();
                    trackTabsClick(label || `Tab ${i + 1}`, String(t.id || i));
                  }
                  setActive(i);
                }}
              >
                {isSchemaTabLabelPlaceholder(t.label) ? "" : t.label}
              </button>
            ))
          ) : null}
        </div>
        {current ? (
          <div className="mte-schema-tab-panel" role="tabpanel">
            {kids.length ? (
              kids.map((child, ci) => (
                <SchemaBlockSection
                  key={String(child.id || ci)}
                  T={T}
                  cfg={child}
                  nested
                />
              ))
            ) : editable ? (
              <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.tabs}</SchemaDraftEmpty>
            ) : null}
          </div>
        ) : null}
      </Shell>
    </TabsClickCtx.Provider>
  );
}

export function SchemaBlockSection({
  T,
  cfg,
  nested,
}: {
  T: ThemeTokens;
  cfg?: SectionCfg;
  nested?: boolean;
}) {
  const { editable } = useEdit();
  const sectionPath = useTemplateSectionInstance();
  const trackTextClick = usePublicSchemaClick("Text");
  const trackVideoClick = usePublicSchemaClick("Video");
  const trackCustomClick = usePublicSchemaClick("Custom");
  const [videoStarted, setVideoStarted] = React.useState(false);
  const kind = String(cfg?.blockType || cfg?.catalogType || "");
  const align = String(cfg?.layout || cfg?.align || "left");
  const heading = String(cfg?.heading || cfg?.title || "").trim();
  const editPrefix = schemaEditPrefix(sectionPath, cfg, nested);

  if (kind === "image") {
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        <GalleryBlock cfg={cfg} />
      </SchemaShell>
    );
  }

  if (kind === "text") {
    const body = String(cfg?.body || cfg?.rich || "");
    const showTitle = cfg?.showTitle !== false;
    const title = showTitle && heading ? heading : "";
    const titlePath = editPrefix ? `${editPrefix}heading` : "";
    const bodyPath = editPrefix ? `${editPrefix}body` : "";
    const showTitleSlot = showTitle && Boolean(title);
    const bodyStored = isSchemaTextBodyPlaceholder(body) ? "" : body;
    const hasRealBody = Boolean(stripHtmlToText(bodyStored));
    const alignCss = textAlign(align);
    const alignClass =
      align === "center" ? " is-center" : align === "right" ? " is-right" : " is-left";

    if (!title && !hasRealBody) {
      if (!editable) return null;
      return (
        <SchemaShell T={T} cfg={cfg} nested={nested}>
          <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.text}</SchemaDraftEmpty>
        </SchemaShell>
      );
    }

    const inner = (
      <div className={`mte-schema-text${alignClass}`}>
        {showTitleSlot ? (
          titlePath ? (
            <E
              path={titlePath}
              as="h2"
              className="mte-schema-text-title"
              style={{ textAlign: alignCss }}
            >
              {title}
            </E>
          ) : (
            <h2
              className="mte-schema-text-title"
              style={{ textAlign: alignCss }}
            >
              {title}
            </h2>
          )
        ) : null}
        <div
          className="mte-schema-text-body"
          style={{ textAlign: alignCss }}
        >
          {hasRealBody ? (
            bodyPath ? (
              <SchemaCanvasHtml
                path={bodyPath}
                html={bodyStored}
                className="mte-schema-rich"
              />
            ) : (
              <div
                className="mte-schema-rich"
                onClick={(event) => schemaAnchorClick(event, trackTextClick)}
                dangerouslySetInnerHTML={{ __html: stripUnsafeHtml(bodyStored) }}
              />
            )
          ) : null}
        </div>
      </div>
    );

    if (!title && !hasRealBody && !editable) return null;
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        {inner}
      </SchemaShell>
    );
  }

  if (kind === "quote") {
    const quote = String(cfg?.quote || "").trim();
    const author = String(cfg?.author || "").trim();
    const marks = cfg?.marks !== false;
    const quotePath = editPrefix ? `${editPrefix}quote` : "";
    const authorPath = editPrefix ? `${editPrefix}author` : "";
    const quoteDisplay = quote.replace(/^"|"$/g, "");

    if (!quoteDisplay && !author) {
      if (!editable) return null;
      return (
        <SchemaShell T={T} cfg={cfg} nested={nested}>
          <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.quote}</SchemaDraftEmpty>
        </SchemaShell>
      );
    }

    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        <blockquote
          className="mte-schema-quote"
          style={{ textAlign: textAlign(align) }}
        >
          {quoteDisplay ? (
            <p>
              {marks ? "“" : ""}
              {quotePath ? (
                <E path={quotePath} as="span" placeholder="Add a quote…">
                  {quoteDisplay}
                </E>
              ) : (
                quoteDisplay
              )}
              {marks ? "”" : ""}
            </p>
          ) : null}
          {author ? (
            <cite>
              —{" "}
              {authorPath ? (
                <E path={authorPath} as="span" placeholder="Author">
                  {author}
                </E>
              ) : (
                author
              )}
            </cite>
          ) : null}
        </blockquote>
      </SchemaShell>
    );
  }

  if (kind === "spacer") {
    const line = String(cfg?.layout || "") === "line";
    const thick = cfg?.thick === true;
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        <div
          className={`mte-schema-spacer${line ? " is-line" : " is-space"}${thick ? " is-thick" : ""}`}
        >
          {line ? (
            <div className={`mte-schema-rule${thick ? " is-thick" : ""}`} />
          ) : null}
        </div>
      </SchemaShell>
    );
  }

  if (kind === "cta") {
    return <CtaBlock T={T} cfg={cfg} nested={nested} />;
  }

  if (kind === "ribbon") {
    if (nested && schemaChildInheritsParentBg(cfg)) {
      return (
        <div className="mte-schema-child">
          <div className="mte-schema-ribbon">
            <RibbonContent cfg={cfg} editable={editable} />
          </div>
        </div>
      );
    }
    if (nested) {
      const bgTone =
        (cfg?.bgTone as "bg" | "surface" | "accent" | undefined) || "bg";
      const ribbonBg =
        bgTone === "surface"
          ? T.surface
          : bgTone === "accent"
            ? T.accent
            : T.bg;
      const ribbonFg = bgTone === "accent" ? T.accentInk : T.ink;
      return (
        <div className="mte-schema-child">
          <div
            className="mte-schema-ribbon"
            style={{ background: ribbonBg, color: ribbonFg }}
          >
            <RibbonContent cfg={cfg} editable={editable} />
          </div>
        </div>
      );
    }
    return <RibbonBlock T={T} cfg={cfg} />;
  }

  if (kind === "video") {
    const url = String(cfg?.url || "").trim();
    const caption = String(cfg?.caption || "").trim();
    const poster = String(cfg?.poster || "").trim();
    const full = String(cfg?.layout || "full") === "full";
    const parsed = url ? parseEmbed(url) : { kind: "empty" as const };
    const inner = (
      <>
        <div
          className={`mte-schema-video${full ? " is-full" : " is-inset"}`}
          style={
            poster && parsed.kind === "empty"
              ? { backgroundImage: `url(${poster})` }
              : undefined
          }
        >
          {parsed.kind === "src" ? (
            <iframe
              title={caption || "Video"}
              src={
                parsed.src +
                (cfg?.autoplay === true || videoStarted
                  ? (parsed.src.includes("?") ? "&" : "?") + "autoplay=1&mute=1"
                  : "")
              }
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : parsed.kind === "html" ? (
            <EmbedHtmlFrame html={parsed.html} title={caption || "Video"} />
          ) : poster ? (
            <button
              type="button"
              className="mte-schema-video-play"
              aria-label={caption || "Play"}
              onClick={() => {
                void trackVideoClick(caption || "Play", "play");
              }}
            >
              <span>▶</span>
            </button>
          ) : null}
          {parsed.kind !== "empty" && !videoStarted && !editable ? (
            <button
              type="button"
              className="mte-schema-video-hit"
              aria-label={caption || "Play"}
              onClick={() => {
                void trackVideoClick(caption || "Play", "play");
                setVideoStarted(true);
              }}
            />
          ) : null}
          {cfg?.controls !== false && parsed.kind === "empty" && poster ? (
            <div className="mte-schema-video-bar" />
          ) : null}
        </div>
        {caption ? (
          <div className="mte-schema-gal-cap">
            {editPrefix ? (
              <E path={`${editPrefix}caption`} placeholder="Add a caption…">
                {caption}
              </E>
            ) : (
              caption
            )}
          </div>
        ) : null}
      </>
    );
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested} fullBleed={full}>
        {inner}
      </SchemaShell>
    );
  }

  if (kind === "discount") {
    const list = (asItems(cfg) as unknown as Record<string, string>[]) || [];
    const demos = SCHEMA_DEMO.discount.items;
    const source = list
      .map((it, i) => {
        const demo = demos[i];
        return {
          ...it,
          brand: demo
            ? schemaDrawerText(it.brand, demo.brand)
            : String(it.brand || "").trim(),
          code: demo
            ? schemaDrawerText(it.code, demo.code)
            : String(it.code || "").trim(),
          desc: demo
            ? schemaDrawerText(it.desc, demo.desc)
            : String(it.desc || "").trim(),
        };
      })
      .filter(
        (it) =>
          String(it.brand || "").trim() ||
          String(it.code || "").trim() ||
          String(it.desc || "").trim() ||
          String(it.expires || "").trim(),
      );
    const rawLayout = String(cfg?.layout || "carousel");
    const layout =
      rawLayout === "inline"
        ? "carousel"
        : rawLayout === "banner"
          ? "stack"
          : rawLayout;
    const title = String(cfg?.heading || "").trim();
    if (!title && !source.length) {
      if (!editable) return null;
      return (
        <SchemaShell T={T} cfg={cfg} nested={nested}>
          <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.discount}</SchemaDraftEmpty>
        </SchemaShell>
      );
    }
    const titlePath = editPrefix ? `${editPrefix}heading` : "";
    const inner = (
      <>
        {title ? (
          <h3 className="mte-schema-text-title">
            {titlePath ? (
              <E path={titlePath} placeholder="Discount Codes">
                {title}
              </E>
            ) : (
              title
            )}
          </h3>
        ) : null}
        <div className={`mte-schema-discount-wrap is-${layout}`}>
          {source.map((it, i) => {
            const cd =
              cfg?.countdown !== false ? countdownText(it.expires || "") : null;
            const expired = cd === "expired";
            const itemKey = String(it.id || i);
            const brandPath = editPrefix
              ? `${editPrefix}items.${itemKey}.brand`
              : "";
            const descPath = editPrefix
              ? `${editPrefix}items.${itemKey}.desc`
              : "";
            const codePath = editPrefix
              ? `${editPrefix}items.${itemKey}.code`
              : "";
            const brand = String(it.brand || "").trim();
            const desc = String(it.desc || "").trim();
            const code = String(it.code || "").trim();
            if (!brand && !desc && !code) return null;
            return (
              <div
                key={itemKey}
                className={`mte-schema-discount${expired ? " is-expired" : ""}`}
              >
                {cfg?.brand !== false && brand ? (
                  <div className="mte-schema-discount-brand">
                    {brandPath ? (
                      <E path={brandPath} placeholder="Brand">
                        {brand}
                      </E>
                    ) : (
                      brand.toUpperCase()
                    )}
                  </div>
                ) : null}
                {desc ? (
                  <div className="mte-schema-discount-desc">
                    {descPath ? (
                      <E path={descPath} placeholder="Description">
                        {desc}
                      </E>
                    ) : (
                      desc
                    )}
                  </div>
                ) : null}
                {code ? (
                  <div className="mte-schema-discount-code">
                    {codePath ? (
                      <E path={codePath} placeholder="CODE">
                        {code}
                      </E>
                    ) : (
                      code
                    )}
                    {expired ? " · EXPIRED" : ""}
                  </div>
                ) : null}
                {cd && !expired ? (
                  <div className="mte-schema-discount-cd">{cd}</div>
                ) : null}
                {cfg?.copy !== false && !expired && code ? (
                  <DiscountCopyButton code={code} />
                ) : null}
              </div>
            );
          })}
        </div>
      </>
    );
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        {inner}
      </SchemaShell>
    );
  }

  if (kind === "testimonials") {
    const headingRaw = String(cfg?.heading || "").trim();
    const list = asItems(cfg) as unknown as Record<string, string>[];
    const pristine =
      editable &&
      schemaItemsArePristine(list, SCHEMA_DEMO.testimonials.items, [
        "quote",
        "name",
      ]) &&
      isSchemaDemoText(headingRaw, SCHEMA_DEMO.testimonials.heading);
    const title = headingRaw;
    const titlePath = editPrefix ? `${editPrefix}heading` : "";
    const paintDemoTitle = pristine;
    const inner = (
      <>
        {title ? (
          paintDemoTitle || !titlePath ? (
            <h3 className="mte-schema-text-title">{title}</h3>
          ) : (
            <E
              path={titlePath}
              as="h3"
              className="mte-schema-text-title"
              placeholder={SCHEMA_DEMO.testimonials.heading}
            >
              {title}
            </E>
          )
        ) : null}
        <TestimonialsBlock cfg={cfg} editPrefix={editPrefix} />
      </>
    );
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        {inner}
      </SchemaShell>
    );
  }

  if (kind === "followers") {
    const demos = SCHEMA_DEMO.followers.items;
    const list = asItems(cfg) as unknown as Record<string, string>[];
    const pristine =
      editable &&
      schemaItemsArePristine(list, SCHEMA_DEMO.followers.items, [
        "platform",
        "count",
      ]);
    const seed: SchemaRow[] = list.length
      ? list
      : pristine
        ? demos.map((_, i) => ({
            id: `demo-${i}`,
            platform: "",
            count: "",
          }))
        : [];
    const source = seed
      .map((it, i): SchemaRow => {
        const demo = demos[i] || demos[0];
        return {
          ...it,
          platform: schemaPreviewText(
            String(it.platform || ""),
            demo?.platform || "",
            pristine,
          ),
          count: schemaPreviewText(String(it.count || ""), demo?.count || "", pristine),
        };
      })
      .filter(
        (it) =>
          String(it.platform || "").trim() || String(it.count || "").trim(),
      );
    const layout = String(cfg?.layout || "inline");
    const title = heading;
    const titlePath = editPrefix ? `${editPrefix}heading` : "";
    if (!title && !source.length && !editable) return null;
    const inner = (
      <>
        {title ? (
          <h3 className="mte-schema-text-title">
            {titlePath ? (
              <E path={titlePath} placeholder="Follower Counts">
                {title}
              </E>
            ) : (
              title
            )}
          </h3>
        ) : null}
        {source.length ? (
        <div className={`mte-schema-followers is-${layout}`}>
          {source.map((it, i) => {
            const itemKey = String(it.id || i);
            const countPath = editPrefix
              ? `${editPrefix}items.${itemKey}.count`
              : "";
            const platformPath = editPrefix
              ? `${editPrefix}items.${itemKey}.platform`
              : "";
            const count = String(it.count || "").trim();
            const platform = String(it.platform || "").trim();
            if (!count && !platform) return null;
            return (
              <div key={itemKey} className="mte-schema-follower">
                {count ? (
                  <b>
                    {countPath ? (
                      <E path={countPath} placeholder="—">
                        {count}
                      </E>
                    ) : (
                      count
                    )}
                  </b>
                ) : null}
                {(cfg?.icons !== false || cfg?.labels !== false) &&
                platform ? (
                  <span className="mte-schema-follower-meta">
                    {cfg?.icons !== false ? (
                      <SchemaFollowerPlatformIcon platform={platform} />
                    ) : null}
                    {cfg?.labels !== false ? (
                      platformPath ? (
                        <E path={platformPath} placeholder="Platform">
                          {platform}
                        </E>
                      ) : (
                        platform.toUpperCase()
                      )
                    ) : null}
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
        ) : editable ? (
          <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.followers}</SchemaDraftEmpty>
        ) : null}
      </>
    );
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        {inner}
      </SchemaShell>
    );
  }

  if (kind === "asseenon") {
    const showTitle = cfg?.showTitle !== false;
    const headingRaw = heading;
    const title = showTitle ? headingRaw : "";
    const inner = (
      <>
        {title ? <h3 className="mte-schema-text-title">{title}</h3> : null}
        <AsSeenOnBlock cfg={cfg} />
      </>
    );
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        {inner}
      </SchemaShell>
    );
  }

  if (kind === "map") {
    const full = String(cfg?.layout || "") === "full";
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested} fullBleed={full}>
        <MapBlock cfg={cfg} />
      </SchemaShell>
    );
  }

  if (kind === "accordion") {
    return <AccordionBlock T={T} cfg={cfg} />;
  }

  if (kind === "sectionheader") {
    const eyebrow = String(cfg?.eyebrow || "").trim();
    const sub = String(cfg?.sub || "").trim();
    const headline = String(cfg?.heading || "").trim();
    if (editable && !eyebrow && !headline && !sub) {
      return (
        <SchemaShell T={T} cfg={cfg} nested={nested}>
          <SchemaDraftEmpty>{SCHEMA_DRAFT_HINT.sectionheader}</SchemaDraftEmpty>
        </SchemaShell>
      );
    }
    const inner = (
      <div
        className="mte-schema-sec-head"
        style={{ textAlign: textAlign(align) }}
      >
        {eyebrow ? (
          <div className="mte-schema-sec-kicker">
            {editPrefix ? (
              <E path={`${editPrefix}eyebrow`} placeholder="Eyebrow">
                {eyebrow}
              </E>
            ) : (
              eyebrow
            )}
          </div>
        ) : null}
        {headline ? (
          <h2 className="mte-schema-sec-headline">
            {editPrefix ? (
              <E path={`${editPrefix}heading`} placeholder="Headline">
                {headline}
              </E>
            ) : (
              headline
            )}
          </h2>
        ) : null}
        {sub ? (
          <p className="mte-tpl-muted-p">
            {editPrefix ? (
              <E path={`${editPrefix}sub`} placeholder="Subtext">
                {sub}
              </E>
            ) : (
              sub
            )}
          </p>
        ) : null}
        {cfg?.divider !== false ? (
          <div
            className={`mte-schema-rule is-short${align === "center" ? " is-center" : ""}`}
          />
        ) : null}
      </div>
    );
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        {inner}
      </SchemaShell>
    );
  }

  if (kind === "columns") {
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested}>
        <ColumnsBlock
          cfg={cfg}
          showEmpty={editable}
          renderChild={(child, ci) => (
            <SchemaBlockSection
              key={String(child.id || ci)}
              T={T}
              cfg={child}
              nested
            />
          )}
        />
      </SchemaShell>
    );
  }

  if (kind === "tabs") {
    return <TabsBlock T={T} cfg={cfg} />;
  }

  if (kind === "embed") {
    const embedRaw = String(cfg?.embedCode || cfg?.url || "");
    const parsed = parseEmbed(embedRaw);
    const full = String(cfg?.layout || "") === "full";
    const audio =
      parsed.kind !== "empty" &&
      isCompactAudioEmbed(
        parsed.kind === "src" ? parsed.src : parsed.kind === "html" ? parsed.html : embedRaw,
      );
    const video =
      parsed.kind === "src" && isFixedAspectVideoEmbed(parsed.src);
    // Pasted HTML (Flodesk) measures itself. Full-bleed non-video URL embeds
    // (e.g. book landing pages) also drop 16:9 so they aren't clipped short.
    const fit =
      !audio &&
      !video &&
      (parsed.kind === "html" ||
        (parsed.kind === "src" &&
          (isSelfSizingEmbedHtml(parsed.src) || full)));
    const viewportFit =
      fit &&
      full &&
      parsed.kind === "src" &&
      !isSelfSizingEmbedHtml(parsed.src);
    const frameClass = `mte-schema-embed-frame${full ? " is-full" : ""}${
      audio ? " is-audio" : ""
    }${fit ? " is-fit" : ""}`;
    const embedClass = `mte-schema-embed${full ? " is-full" : ""}${
      audio ? " is-audio" : ""
    }${fit ? " is-fit" : ""}`;
    return (
      <SchemaShell T={T} cfg={cfg} nested={nested} fullBleed={full}>
        {parsed.kind === "empty" ? (
          <div className={`mte-schema-embed-ph${full ? " is-full" : ""}`} />
        ) : parsed.kind === "src" ? (
          <EmbedHtmlFrame
            src={parsed.src}
            title="Embed"
            className={frameClass}
            viewportFit={viewportFit}
          />
        ) : (
          <EmbedHtmlFrame
            html={parsed.html}
            title="Embed"
            className={embedClass}
          />
        )}
      </SchemaShell>
    );
  }

  if (kind === "vibe") {
    const html = stripUnsafeHtml(String(cfg?.vibeOutput || cfg?.html || ""));
    const full = String(cfg?.layout || "") === "full";
    const thinking = Boolean(cfg?.vibeBusy);
    const pad = full ? "0" : "20px 30px";
    const wrap = (node: React.ReactNode) => (
      <SchemaShell T={T} cfg={cfg} nested={nested} fullBleed={full}>
        <div style={{ padding: pad }}>{node}</div>
      </SchemaShell>
    );
    if (thinking && !html) {
      return wrap(
        <div className={`mte-schema-vibe-box${full ? " is-full" : ""}`}>
          <div className="mte-schema-vibe-busy">✨ Thinking…</div>
        </div>,
      );
    }
    if (!html) {
      if (!editable && !nested) return null;
      return wrap(
        <div className={`mte-schema-embed-ph${full ? " is-full" : ""}`} />,
      );
    }
    return wrap(
      <>
        <div
          className={`mte-schema-vibe mte-schema-vibe-box${full ? " is-full" : ""}`}
          onClick={(event) => schemaAnchorClick(event, trackCustomClick)}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        {thinking ? (
          <div className="mte-schema-vibe-writing">writing…</div>
        ) : null}
      </>,
    );
  }

  return (
    <SchemaShell T={T} cfg={cfg} nested={nested}>
      {null}
    </SchemaShell>
  );
}

SchemaBlockSection.options = {
  bgTone: {
    kind: "radio",
    label: "Background",
    options: ["bg", "surface", "accent"],
    default: "bg",
  },
} as SectionOptions;
